/**
 * Central configuration file for all game constants.
 * Modify values here to adjust game behaviour without changing logic files.
 */

export const CAMERA_CONFIG = {
  /** Initial horizontal angle (radians) */
  alpha: 8.49,
  /** Initial vertical angle (radians) */
  beta: 1.39,
  /** Initial distance from target */
  radius: 2.5,
  speed: 0.1,
  wheelPrecision: 50,
  minZ: 0.1,
  lowerRadiusLimit: 0,
  upperRadiusLimit: 10,
  lowerBetaLimit: 0.3,
  upperBetaLimit: Math.PI / 2,
  /** Radius below which first-person mode activates */
  firstPersonThreshold: 1.5,
  /** Eye height above player origin for first-person view */
  headOffset: 1.12,
  /** Mouse movement sensitivity in pointer-lock mode */
  rotationSensitivity: 0.003,
  /** Cinematic intro: starting radius */
  introStartRadius: 4,
  /** Cinematic intro: ending radius */
  introTargetRadius: 2.5,
  /** Cinematic intro: duration in frames (at 60 fps) */
  introDurationFrames: 150,
};

export const PLAYER_CONFIG = {
  startPosition: { x: -2.79, y: 0.39, z: 6.52 },
  /** Initial Y-axis rotation (radians) */
  startRotationY: 8.49,
  /** Uniform scale applied to the loaded GLB mesh */
  modelScale: 0.5,
  walkSpeed: 1.125,
  runSpeed: 3.75,
  walkBackwardSpeed: 1.125,
  walkAnimSpeed: 1,
  runAnimSpeed: 1,
  jumpForce: 4.5,
  gravityForce: 12,
  /** Player respawns when position.y drops below this */
  respawnBelowY: -2.5,
  /** Upward ray height used for wall-blocked checks */
  wallCheckHeights: [0.2, 0.5, 0.8, 1.2] as number[],
  /** Wall-ray probe distance */
  wallCheckDistance: 0.4,
};

export const SCENE_CONFIG = {
  gravity: { x: 0, y: -0.05, z: 0 },
  animationBlendingSpeed: 0.2,
  environmentIntensity: 0.6,
  backgroundColor: { r: 0, g: 0, b: 0, a: 1 },
};

export const LIGHTS_CONFIG = {
  hemi: {
    intensity: 0.5,
    diffuse: { r: 1.0, g: 0.4, b: 0.1 },
    groundColor: { r: 0.5, g: 0.15, b: 0.3 },
  },
  sun: {
    direction: { x: -1, y: -0.3, z: 0.5 },
    intensity: 2.0,
    diffuse: { r: 1.0, g: 0.3, b: 0.05 },
    specular: { r: 1.0, g: 0.4, b: 0.1 },
  },
};

export const SEA_CONFIG = {
  positionY: -2.5,
  width: 500,
  height: 500,
  subdivisions: 16,
  bumpTextureUrl: "https://assets.babylonjs.com/textures/waterbump.png",
  bumpHeight: 0.75,
  waveHeight: 0.3,
  waveLength: 0.08,
  waveSpeed: 25,
  windForce: 8,
  windDirection: { x: 1, y: 0.1 },
  colorBlendFactor: 0.3,
  waterColor: { r: 0.0, g: 0.5, b: 0.6 },
  waterColor2: { r: 0.0, g: 0.25, b: 0.4 },
};

export const ISLAND_CONFIG = {
  modelFile: "island.glb",
  modelFolder: "./models/",
  scaling: { x: -0.60, y: 0.60, z: 0.60 },
};

export const PLAYER_MODEL_CONFIG = {
  modelFile: "player.glb",
  modelFolder: "./models/",
};

export const ENVIRONMENT_CONFIG = {
  files: [
    "./environment/hdr_fantasysky (2).env",
    "./environment/hdr_fantasysky (6).env",
    "./environment/hdr_fantasysky (10).env",
    "./environment/hdr_fantasysky (11).env",
    "./environment/hdr_fantasysky (12).env",
    "./environment/hdr_fantasysky (19).env",
    "./environment/hdr_fantasysky (20).env",
  ],
  scale: 5000,
  /** Seconds to hold each skybox fully visible before starting the next crossfade */
  holdSeconds: 45,
  /** Seconds the crossfade transition takes */
  fadeSeconds: 10,
};

export const INTERACTIVE_CONFIG = {
  contactTable: {
    /** Mesh-name substring to recognise the contact-table mesh */
    meshNameContains: "contacttable",
    proximityThreshold: 1.5,
  },
  workspace: {
    /** Mesh-name substrings to recognise the workspace mesh */
    meshNameContains: ["workspace", "wordspace"],
    proximityThreshold: 2,
  },
  boat: {
    /** Mesh-name substrings to recognise the boat mesh */
    meshNameContains: ["boat"],
    proximityThreshold: 1.5,
  },
  skillStone: {
    /** Mesh-name substrings to recognise the skill stone mesh */
    meshNameContains: ["skillstone"],
    proximityThreshold: 1.5,
  },
  inProgressSign: {
    /** Mesh-name substrings to recognise the in-progress sign mesh */
    meshNameContains: ["inprogresssign"],
    proximityThreshold: 1.5,
  },
};
